import { useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type { BankReconciliation, CashLocation } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import {
  Empty,
  ErrorBanner,
  Loading,
  Money,
  Panel,
  endOfMonth,
  formatDate,
  money,
} from '../components/ui'

/**
 * The bank reconciliation — the control BUILD_PLAN §3.3 names when it says the book's closing
 * figure has to tie to the bank statement or the physical count.
 *
 * <p>The three columns behind it shipped with the first migration and an endpoint could set them
 * from the day the cash books existed. Nothing called it and nothing read the result, so a book
 * could be ticked row by row with no screen any the wiser.</p>
 *
 * <p>The arithmetic is deliberately shown rather than just its answer. "Out by 4,300" sends
 * somebody through a year of rows; "cheque 1182, 62 days, not cleared" is the same fact with the
 * search already done.</p>
 */
export function BankReconciliationPage() {
  const { can } = useAuth()
  const [locationId, setLocationId] = useState('')
  const [asOf, setAsOf] = useState(endOfMonth())
  const [statement, setStatement] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  const locations = useQuery<CashLocation[]>('/cash-books/locations')

  // Only banks. A safe is reconciled against a physical count, which is the same arithmetic with
  // no statement to type — worth having, and not worth pretending this screen already does it.
  const banks = (locations.data ?? []).filter((l) => l.type === 'Bank')
  const chosen = banks.find((l) => l.id === locationId) ?? banks[0]

  // The statement balance is a number the user is reading off paper. Sent as 0 until they type
  // one, so the outstanding list is useful before the difference is.
  const statementBalance = Number(statement) || 0

  const report = useQuery<BankReconciliation>(
    chosen ? `/cash-books/${chosen.id}/reconciliation${query({ asOf, statementBalance })}` : null,
    [chosen?.id, asOf, statementBalance],
  )

  function toggle(id: string) {
    setSelected((previous) => {
      const next = new Set(previous)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  /** Ticks the selected rows off in one call, so a half-applied statement is not possible. */
  async function reconcile() {
    setBusy(true)
    setError(undefined)
    try {
      await api('/cash-books/reconcile', {
        method: 'POST',
        body: { rowIds: [...selected], reconciled: true },
      })
      setSelected(new Set())
      report.reload()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Bank account</label>
          <select value={chosen?.id ?? ''} onChange={(e) => setLocationId(e.target.value)}>
            {banks.map((location) => (
              <option key={location.id} value={location.id}>
                {location.nameEn} ({location.currency})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Statement date</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
        <div className="field">
          <label>Closing balance per statement</label>
          <input
            type="number"
            step="0.01"
            value={statement}
            placeholder="0.00"
            onChange={(e) => setStatement(e.target.value)}
          />
        </div>

        <div className="spacer" />

        {can(P.cashBook.reconcile) && selected.size > 0 && (
          <button className="primary" disabled={busy} onClick={() => void reconcile()}>
            {busy ? 'Marking…' : `Mark ${selected.size} as cleared`}
          </button>
        )}
      </div>

      <ErrorBanner error={locations.error} />
      <ErrorBanner error={report.error} />
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {!locations.loading && banks.length === 0 && (
        <Empty>
          No bank accounts yet. Add one on <strong>Banks &amp; safes</strong> — a safe is reconciled
          against a physical count rather than a statement.
        </Empty>
      )}

      {report.loading && <Loading />}

      {report.data && (
        <>
          {statement === '' ? (
            <div className="banner info">
              Type the closing balance from the statement to see whether the book agrees. The
              outstanding items below are already correct without it.
            </div>
          ) : report.data.isReconciled ? (
            <div className="banner ok">
              <strong>Reconciled.</strong> The statement agrees with everything cleared to{' '}
              {formatDate(report.data.asOf)}, with {report.data.outstanding.length} item
              {report.data.outstanding.length === 1 ? '' : 's'} still to appear.
            </div>
          ) : (
            <div className="banner error">
              <strong>Out by {money(report.data.difference)}.</strong> The statement does not agree
              with what has been cleared. Something is on one side and not the other — a charge or
              an interest line not yet entered, or a row ticked that the bank has not seen.
            </div>
          )}

          {/* The arithmetic, shown rather than asserted. This is the shape of the paper form the
              office already fills in, which is the point of BUILD_PLAN §3.3. */}
          <Panel title="Reconciliation">
            <table>
              <tbody>
                <tr>
                  <td>Balance per statement</td>
                  <td className="num">
                    <Money value={report.data.statementBalance} />
                  </td>
                </tr>
                <tr>
                  <td>Less cleared per the books</td>
                  <td className="num">
                    <Money value={-report.data.reconciledBalance} />
                  </td>
                </tr>
                <tr className="subtotal">
                  <td>Difference</td>
                  <td className={`num ${report.data.isReconciled ? 'positive' : 'negative'}`}>
                    <Money value={report.data.difference} />
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} style={{ height: 10 }} />
                </tr>
                <tr>
                  <td>Cleared balance</td>
                  <td className="num">
                    <Money value={report.data.reconciledBalance} />
                  </td>
                </tr>
                <tr>
                  <td>Add receipts not yet on the statement</td>
                  <td className="num">
                    <Money value={report.data.unreconciledReceipts} />
                  </td>
                </tr>
                <tr>
                  <td>Less payments not yet presented</td>
                  <td className="num">
                    <Money value={-report.data.unreconciledPayments} />
                  </td>
                </tr>
                <tr className="subtotal">
                  <td>Balance per the books</td>
                  <td className="num">
                    <Money value={report.data.bookBalance} />
                  </td>
                </tr>
              </tbody>
            </table>
          </Panel>

          <Panel title={`Outstanding at ${formatDate(report.data.asOf)}`} flush>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }} />
                    <th style={{ width: 90 }}>Date</th>
                    <th style={{ width: 110 }}>Ref</th>
                    <th>Description</th>
                    <th style={{ width: 150 }}>Party</th>
                    <th style={{ width: 70 }} className="num">
                      Age
                    </th>
                    <th style={{ width: 120 }} className="num">
                      Receipt
                    </th>
                    <th style={{ width: 120 }} className="num">
                      Payment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.outstanding.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <Empty>Everything to this date has cleared.</Empty>
                      </td>
                    </tr>
                  )}

                  {report.data.outstanding.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {can(P.cashBook.reconcile) && (
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            onChange={() => toggle(item.id)}
                          />
                        )}
                      </td>
                      <td>{formatDate(item.date)}</td>
                      <td className="code">{item.ref ?? '—'}</td>
                      <td>{item.description}</td>
                      <td>{item.partyName ?? '—'}</td>
                      {/* Over two months outstanding is a stale cheque or a mistake, not a
                          timing difference — which is a different conversation with the bank. */}
                      <td className={`num ${item.ageInDays > 60 ? 'negative' : ''}`}>
                        {item.ageInDays}d
                      </td>
                      <td className="num">
                        <Money value={item.receipt} blankZero />
                      </td>
                      <td className="num">
                        <Money value={item.payment} blankZero />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  )
}
